"use client"

import React, { useState, useEffect, useMemo, useCallback } from "react"
import { motion } from "framer-motion"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card"
import { Input } from "../ui/input"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { Button } from "../ui/button"
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Settings2,
  Table as TableIcon,
  ChevronUp,
  ChevronsUpDown,
  Search,
} from "lucide-react"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { TableData, TableConfig, TableSuggestion, ColumnDef as TableColumnDef } from "../lib/tools/table-tools"

interface AIDataTableRendererProps {
  tableData: {
    data: TableData[]
    config: TableConfig
    suggestions?: TableSuggestion[]
    show_suggestions?: boolean
  }
}

export default function AIDataTableRenderer({ tableData }: AIDataTableRendererProps) {
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<TableData[]>([])
  const [config, setConfig] = useState<TableConfig | null>(null)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})
  const [globalFilter, setGlobalFilter] = useState("")
  const [pageSize, setPageSize] = useState(10)

  // Process and validate table data on mount
  useEffect(() => {
    try {
      console.log("TABLE RENDERER: Initializing with tableData:", tableData);
      
      if (!tableData) {
        setError("No table data provided")
        return
      }

      const { data, config, suggestions } = tableData
      
      console.log("TABLE RENDERER: Initial data:", data?.length || 0, "rows");
      console.log("TABLE RENDERER: Initial config:", config);
      console.log("TABLE RENDERER: Initial suggestions:", suggestions);
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        setError("Table data is missing or empty")
        return
      }

      if (!config) {
        setError("Table configuration is missing")
        return
      }
      
      // Set data immediately
      setData(data)
      
      // Handle case where config columns don't match data fields
      if (!config.columns || config.columns.length === 0) {
        console.log("TABLE RENDERER: No columns provided, generating from data")
        // Generate columns from data
        const firstItem = data[0]
        const generatedColumns = Object.keys(firstItem).map(key => ({
          accessorKey: key,
          header: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
          enableSorting: true,
          enableFiltering: true
        }))
        
        // Create updated config with generated columns
        const updatedConfig = {
          ...config,
          columns: generatedColumns
        }
        setConfig(updatedConfig)
      } else {
        // Check if the column accessorKeys match the actual data fields
        const firstItem = data[0]
        const dataFields = Object.keys(firstItem)
        const configFields = config.columns.map((col: TableColumnDef) => col.accessorKey)
        
        // Check for mismatches
        const mismatched = configFields.some((field: string) => !dataFields.includes(field))
        
        if (mismatched) {
          console.log("TABLE RENDERER: Column definitions don't match data fields, adapting columns")
          console.log("TABLE RENDERER: Data fields:", dataFields)
          console.log("TABLE RENDERER: Config fields:", configFields)
          
          // Map config columns to data fields
          // Strategy: Try to find a matching field in data or use index-based matching
          const adaptedColumns = dataFields.map((field, index) => {
            // Try to find an existing column from config that might match
            // Use the field from data as fallback
            let adaptedColumn
            
            if (index < config.columns.length) {
              // Reuse properties from the original column but update the accessorKey
              adaptedColumn = {
                ...config.columns[index],
                accessorKey: field
              }
            } else {
              // Create a new column definition
              adaptedColumn = {
                accessorKey: field,
                header: field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1'),
                enableSorting: true,
                enableFiltering: true,
                enableHiding: true
              }
            }
            
            return adaptedColumn
          })
          
          // Update the config with adapted columns
          const updatedConfig = {
            ...config,
            columns: adaptedColumns
          }
          setConfig(updatedConfig)
        } else {
          // Config columns match data fields, use as is
          setConfig(config)
        }
      }
      
      setError(null)
    } catch (err) {
      console.error("TABLE RENDERER: Error initializing table", err)
      setError("Failed to initialize table data")
    }
  }, [tableData])

  // Create columns with proper accessors for sorting
  const columns = useMemo(() => {
    if (!config?.columns) return [];
    
    return config.columns.map((column: TableColumnDef) => ({
      id: column.accessorKey,
      accessorKey: column.accessorKey,
      header: column.header || column.accessorKey.charAt(0).toUpperCase() + 
              column.accessorKey.slice(1).replace(/([A-Z])/g, ' $1'),
      enableSorting: column.enableSorting !== false,
      enableHiding: column.enableHiding !== false,
      cell: (info: any) => info.getValue(),
    }));
  }, [config?.columns]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
  })

  // Handler for table suggestion clicks
  const handleSuggestionClick = useCallback((event: CustomEvent) => {
    if (event.detail?.query) {
      console.log("Table suggestion clicked:", event.detail.query);
      
      // Send suggestion to parent via a custom event
      const suggestionEvent = new CustomEvent('ai-table-suggestion', {
        bubbles: true,
        detail: { query: event.detail.query }
      });
      document.dispatchEvent(suggestionEvent);
    }
  }, []);
  
  // Add and remove event listener for table suggestions
  useEffect(() => {
    // Cast to any to work with CustomEvent
    window.addEventListener('table-suggestion-click', handleSuggestionClick as any);
    
    return () => {
      window.removeEventListener('table-suggestion-click', handleSuggestionClick as any);
    };
  }, [handleSuggestionClick]);

  // Render the suggestions section if available
  const renderSuggestions = () => {
    // Check if suggestions are enabled and available
    const suggestionsEnabled = config?.show_suggestions === true;
    const hasSuggestions = config?.suggestions && Array.isArray(config.suggestions) && config.suggestions.length > 0;
    
    if (!suggestionsEnabled || !hasSuggestions) {
      return null;
    }
    
    return (
      <div className="mt-4">
        <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
          Alternative Table Suggestions
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(config.suggestions as TableSuggestion[]).map((suggestion, index) => (
            <TableSuggestionCard key={index} suggestion={suggestion} />
          ))}
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
      >
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <Card className="relative overflow-hidden">
        {/* Improved header layout */}
        <CardHeader className="bg-muted/20 pb-2 px-4 pt-4">
          <div className="flex flex-col space-y-3">
            <div className="flex items-center gap-1.5">
              <TableIcon className="h-4 w-4" />
              <CardTitle className="text-lg font-medium">
                {config?.title || "Data Table"}
              </CardTitle>
            </div>
            {config?.description && (
              <CardDescription className="text-sm text-muted-foreground">
                {config.description}
              </CardDescription>
            )}
          </div>
        </CardHeader>

        {/* Search and view controls in a separate section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 py-3 border-b">
          {config?.showSearch !== false && (
            <div className="relative w-full sm:w-64 mb-2 sm:mb-0">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={config?.searchPlaceholder || "Search..."}
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-8 w-full h-9"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <Settings2 className="h-4 w-4 mr-1" />
                  <span>Columns</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id.replace(/([A-Z])/g, ' $1').trim()}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <span>Rows: {pageSize}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {[5, 10, 20, 50, 100].map((size) => (
                  <DropdownMenuCheckboxItem
                    key={size}
                    className="capitalize"
                    checked={pageSize === size}
                    onCheckedChange={() => {
                      setPageSize(size);
                      table.setPageSize(size);
                    }}
                  >
                    {size} rows
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <CardContent className="p-0">
          <div className="rounded-md">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id} className="py-3 px-4">
                          {header.isPlaceholder ? null : (
                            <div 
                              className="flex items-center gap-1 cursor-pointer"
                              onClick={() => header.column.getCanSort() && header.column.toggleSorting()}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {header.column.getCanSort() && (
                                <div className="ml-1 shrink-0">
                                  {header.column.getIsSorted() === "asc" ? (
                                    <ChevronUp className="h-3 w-3" />
                                  ) : header.column.getIsSorted() === "desc" ? (
                                    <ChevronDown className="h-3 w-3" />
                                  ) : (
                                    <ChevronsUpDown className="h-3 w-3 opacity-30" />
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} className="py-3 px-4">
                            {cell.column.id.toLowerCase().includes('date') ? (
                              formatDateValue(flexRender(cell.column.columnDef.cell, cell.getContext()))
                            ) : cell.column.id.toLowerCase().includes('status') || 
                               ['status', 'type', 'category'].includes(cell.column.id.toLowerCase()) ? (
                              <StatusCell value={flexRender(cell.column.columnDef.cell, cell.getContext())} />
                            ) : (
                              String(flexRender(cell.column.columnDef.cell, cell.getContext()) ?? '')
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={table.getAllColumns().length}
                        className="h-24 text-center"
                      >
                        No results.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          
          {config?.showPagination !== false && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 py-4 px-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )}{" "}
                of {table.getFilteredRowModel().rows.length} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <span className="text-sm mx-2">
                  Page {table.getState().pagination.pageIndex + 1} of{" "}
                  {table.getPageCount()}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="text-xs text-muted-foreground bg-muted/10 py-3 px-4 border-t">
          {data.length} total records
        </CardFooter>
      </Card>
      
      {/* Render suggestions below the table */}
      {renderSuggestions()}
    </motion.div>
  )
}

// Add formatting helper functions
// Format date values for better display
const formatDateValue = (value: any): React.ReactNode => {
  if (!value) return value;
  
  // If value is already a Date object or timestamp
  let dateStr = String(value);
  
  try {
    // Try to parse and format the date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString();
    }
  } catch (e) {
    // If parsing fails, return the original value
  }
  
  return dateStr;
};

// Display status badges for status-like fields
const StatusCell = ({ value }: { value: any }): React.ReactNode => {
  if (!value) return null;
  
  const val = String(value).toLowerCase();
  let bgClass = "bg-gray-100 text-gray-800";
  
  // Assign colors based on common status values
  if (['active', 'completed', 'approved', 'success', 'yes', 'true'].includes(val)) {
    bgClass = "bg-green-100 text-green-800";
  } else if (['pending', 'in progress', 'waiting', 'reviewing'].includes(val)) {
    bgClass = "bg-yellow-100 text-yellow-800";
  } else if (['inactive', 'failed', 'rejected', 'error', 'no', 'false'].includes(val)) {
    bgClass = "bg-red-100 text-red-800";
  } else if (['draft'].includes(val)) {
    bgClass = "bg-blue-100 text-blue-800";
  }
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgClass}`}>
      {value}
    </span>
  );
};

// Enhanced suggestion card component that properly handles clicks
const TableSuggestionCard = ({ suggestion }: { suggestion: TableSuggestion }) => {
  const handleClick = () => {
    console.log("TABLE SUGGESTION: Clicked suggestion:", suggestion.query);
    
    // Create a custom event for the chat window to capture
    const event = new CustomEvent('ai-suggestion-click', {
      bubbles: true,
      detail: { 
        query: suggestion.query,
        source: 'table-suggestion'
      }
    });
    
    // Dispatch on document to ensure it bubbles up to parent components
    document.dispatchEvent(event);
    
    // Also try dispatching on window as a fallback
    window.dispatchEvent(new CustomEvent('table-suggestion-click', {
      bubbles: true,
      detail: { query: suggestion.query }
    }));
    
    // Log after dispatch to confirm execution
    console.log("TABLE SUGGESTION: Dispatched suggestion event");
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <Card 
        className="flex flex-col h-full cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
        onClick={handleClick}
      >
        <CardHeader className="p-4 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TableIcon className="h-4 w-4" />
              <CardTitle className="text-sm font-medium">
                {suggestion.title || `${suggestion.tableType.charAt(0).toUpperCase() + suggestion.tableType.slice(1)} Table`}
              </CardTitle>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent className="px-4 py-2 text-xs text-muted-foreground">
          {suggestion.reason}
        </CardContent>
      </Card>
    </motion.div>
  );
}; 